import React from 'react';
import { css } from 'styled-components';

import { Share, CalendarOutline, ClockO } from '@crehana/icons';
import { PrimaryButton, Dialog, Avatar, colors } from '@crehana/ui';
import { useOpenState } from '@crehana/react-hooks';

import { useCrehanaLiveTranslations } from 'Jsx/CrehanaLive/hooks';
import { formatDate } from 'Jsx/CrehanaLive/utils';
import { LiveNode } from 'Jsx/CrehanaLive/graphql/types/LiveNode';
import LiveTag from 'Jsx/CrehanaLive/components/OnLiveTag';

import ShareDialogContent from './ShareDialogContent';

interface VideoInfoProps extends LiveNode {
  isFuture?: boolean;
  liveIsActive?: boolean;
  liveHasStarted?: boolean;
  enrollToLive?: (liveId: number) => Promise<void>;
  isForbiddenPremiumLive?: boolean;
}

const VideoInfo: React.FC<VideoInfoProps> = ({
  enrollToLive,
  originalId,
  liveCategory,
  title,
  presenter,
  presenters,
  startLive,
  slug,
  calendarLink,
  isFuture,
  liveIsActive,
  liveHasStarted,
  isSubscribed,
  tags,
  isForbiddenPremiumLive,
}) => {
  const { t } = useCrehanaLiveTranslations();
  const [isDialogOpen, setIsDialogOpen] = useOpenState({
    defaultOpen: false,
  });
  const [isLoading, setIsLoading] = React.useState(false);
  const speakers = React.useMemo(() => {
    if (presenters?.edges.length) {
      return presenters.edges.map(e => e!.node!);
    }
    if (presenter) {
      return [presenter];
    }
    return [];
  }, [presenter, presenters]);

  const speakersNames = speakers.map(({ fullName }) => fullName).join(' + ');

  const isMultiPresenter = speakers.length > 1;

  const liveDateInfoJsx = (
    <>
      {speakers.length ? (
        <div className="flex flex-wrap items-center mr-16">
          <div
            className="relative h-24"
            style={{ width: 24 + speakers.length * 12 }}
          >
            {speakers.map((speaker, i) => (
              <Avatar
                key={speaker.originalId!}
                src={speaker.thumbnailUrl}
                size={24}
                className="absolute"
                style={{ left: 12 * i }}
              />
            ))}
          </div>
          {isMultiPresenter ? (
            <span className="font-body3 text-white">{speakersNames}</span>
          ) : (
            <span className="font-caption text-base-lighter">
              {speakersNames}
            </span>
          )}
        </div>
      ) : null}

      <span className="items-center hidden sm:flex">
        <CalendarOutline color={colors.baseLighter} size={13} />
        <span className="inline-block ml-8 font-caption text-base-lighter">
          {formatDate(startLive).dayAndMonth}
        </span>
      </span>
      <span className="items-center hidden sm:flex">
        <span className="flex ml-16">
          <ClockO size={13} color={colors.baseLighter} />
        </span>
        <span className="inline-block ml-8 font-caption text-base-lighter">
          {formatDate(startLive).minutesAndHours}{' '}
        </span>
      </span>
    </>
  );

  const canRenderPrimaryButton =
    isFuture &&
    !isForbiddenPremiumLive &&
    ((enrollToLive && !isSubscribed) || calendarLink);

  const renderPrimaryButton = () => {
    if (enrollToLive && !isSubscribed) {
      return (
        <PrimaryButton
          full
          label={
            tags?.includes('live-day')
              ? t('SIGN_UP_FREE_LABEL')
              : t('SIGN_UP_LIVE_LABEL')
          }
          disabled={isLoading}
          onClick={() => {
            // it is validated on render
            if (enrollToLive) {
              setIsLoading(true);
              enrollToLive(originalId!)
                .then(() => setIsLoading(false))
                .catch(() => setIsLoading(false));
            }
          }}
          isDark
        />
      );
    }
    if (calendarLink) {
      return (
        <PrimaryButton
          label={t('ADD_CALENDAR_LABEL')}
          href={calendarLink}
          target="_blank"
          rel="noopener noreferrer"
          full
          isDark
        />
      );
    }
    return null;
  };

  return (
    <>
      <div className="flex flex-row items-start justify-between md:flex-col lg:flex-row">
        <div className="mr-24">
          {liveCategory && (
            <p className="font-label text-base-lighter w-full mb-8">
              {liveCategory.name}
            </p>
          )}
          <h1 className="font-h4 text-white">{title}</h1>
          <div className="flex items-center mt-16">{liveDateInfoJsx}</div>
        </div>

        <div className="flex sm:flex-col md:flex-row lg:flex-col md:mt-20 lg:m-0 items-center">
          {liveIsActive && liveHasStarted && (
            <div>
              <LiveTag
                light
                style={{
                  width: 84,
                  height: 40,
                  paddingTop: 10.5,
                  paddingBottom: 10.5,
                }}
              />
            </div>
          )}

          {canRenderPrimaryButton && (
            <div
              className="hidden sm:block w-full mt-24 md:mt-0 mr-24"
              css={css`
                ${props => props.theme.breakpoints.up('md')} {
                  width: 230px;
                }
              `}
            >
              {renderPrimaryButton()}
            </div>
          )}

          <button
            type="button"
            className="flex items-center sm:mt-20 md:mt-0 lg:mt-20 outline-none"
            onClick={() => setIsDialogOpen(true)}
          >
            <Share size={16} color={colors.baseLighter} className="mr-8" />
            <span className="hidden sm:block font-label text-base-lighter whitespace-no-wrap">
              {t('SHARE_BUTTON_LIVE_LABEL')}
            </span>
          </button>
        </div>
      </div>

      {canRenderPrimaryButton && (
        <div
          className="block sm:hidden w-full mt-24 md:mt-0"
          css={css`
            ${props => props.theme.breakpoints.up('md')} {
              width: 230px;
            }
          `}
        >
          {renderPrimaryButton()}
        </div>
      )}

      <Dialog open={isDialogOpen} onDismiss={() => setIsDialogOpen(false)}>
        <ShareDialogContent liveUrl={`https://www.crehana.com/live/${slug}/`} />
      </Dialog>
    </>
  );
};

export default VideoInfo;
